import { useEffect, useMemo, useRef, useState } from "react"
import { useCalendarIntegration } from "@/modules/Calendar/hooks/useCalendarIntegration"
import { useForm, type Control } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { useToast } from "@/contexts/ToastContext"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { FeedbackModal } from "@/modules/Feedback/components/FeedbackModal"
import InputMask from "react-input-mask"
import {
  getUserProfile,
  updateUserProfile,
  getAreasAtuacao,
  getCidades,
  uploadProfileImage,
} from "@/modules/Auth/service/UserProfileService"
import type {
  UserProfileFormData,
  AreaAtuacaoItem,
  CidadeItem,
} from "@/modules/Auth/service/UserProfileService"
import { Button } from "@/components/ui/button"
import { cn } from "@/utils/utils"
import { Icon } from "@/components/Atoms/Icon"

const SIZES = [
  { value: "PP", label: "PP" },
  { value: "P", label: "P" },
  { value: "M", label: "M" },
  { value: "G", label: "G" },
  { value: "GG", label: "GG" },
  { value: "EG", label: "EG" },
  { value: "EGG", label: "EGG" },
]

const MEMBER_TYPES = [
  { value: "1", label: "Giants" },
  { value: "2", label: "Giants Sócio" },
  { value: "3", label: "Giants Investidor" },
]

function normalizeStr(str: string): string {
  if (!str) return ""
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

interface FormFieldDisplayProps {
  label: string
  value?: string | null | number
}

const FormFieldDisplay: React.FC<FormFieldDisplayProps> = ({ label, value }) => (
  <div className="flex flex-col space-y-1">
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    <span className="text-base text-foreground">{value || "-"}</span>
  </div>
)

interface RadioGroupFieldProps {
  control: Control<any>
  name: string
  label: string
  options: { value: string; label: string }[]
  disabled?: boolean
}

const RadioGroupField: React.FC<RadioGroupFieldProps> = ({
  control,
  name,
  label,
  options,
  disabled,
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className="space-y-3">
        <FormLabel>{label}</FormLabel>
        <FormControl>
          <RadioGroup
            onValueChange={field.onChange}
            value={field.value}
            className="flex flex-wrap gap-2"
            disabled={disabled}
          >
            {options.map((option) => (
              <FormItem
                key={option.value}
                className="flex items-center space-x-0"
              >
                <FormControl>
                  <RadioGroupItem
                    value={option.value}
                    id={`${name}-${option.value}`}
                    className="sr-only"
                  />
                </FormControl>
                <FormLabel
                  htmlFor={`${name}-${option.value}`}
                  className={cn(
                    "cursor-pointer rounded-md border px-4 py-2 text-sm transition-colors",
                    "border-gray-200 hover:bg-gray-100 hover:text-zinc-900",
                    field.value === option.value &&
                      "border-orange-500 bg-orange-500/10 font-semibold text-orange-600"
                  )}
                >
                  {option.label}
                </FormLabel>
              </FormItem>
            ))}
          </RadioGroup>
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
)

function FaceIdSection() {
  const toast = useToast()
  const [faceIdEnabled, setFaceIdEnabled] = useState(
    !!localStorage.getItem("faceIdRefreshToken")
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data
        if (data?.type === "FACE_AUTH_SUCCESS") {
          const rt = localStorage.getItem("refreshToken")
          const userStr = localStorage.getItem("user")
          if (rt && userStr) {
            localStorage.setItem("faceIdRefreshToken", rt)
            localStorage.setItem("faceIdUser", userStr)
            setFaceIdEnabled(true)
            toast.success("Face ID habilitado com sucesso!")
          }
          setLoading(false)
        } else if (data?.type === "FACE_AUTH_SETTINGS_REQUIRED") {
          toast.danger("Permissão negada. Habilite câmera e Face ID nas Configurações do dispositivo.")
          setLoading(false)
        } else if (data?.type === "FACE_AUTH_ERROR") {
          toast.danger("Não foi possível verificar sua identidade")
          setLoading(false)
        }
      } catch {}
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  const handleEnable = () => {
    setLoading(true)
    ;(window as any).ReactNativeWebView.postMessage(
      JSON.stringify({ type: "REQUEST_FACE_AUTH" })
    )
  }

  const handleDisable = () => {
    localStorage.removeItem("faceIdRefreshToken")
    localStorage.removeItem("faceIdUser")
    setFaceIdEnabled(false)
    toast.success("Face ID desabilitado")
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
        <Icon name="fingerprint" />
        Segurança
      </h3>
      <div className="card-premium p-3 sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-white">Login com Face ID</p>
            <p className="text-xs text-zinc-500">
              {faceIdEnabled
                ? "Habilitado — você entra automaticamente ao abrir o app"
                : "Desabilitado — ative para entrar com biometria"}
            </p>
          </div>
          {faceIdEnabled ? (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                ativo
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleDisable}
                className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400"
              >
                Desabilitar
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={handleEnable}
              className="shrink-0 border-white/10 bg-transparent text-white hover:bg-white/10 disabled:opacity-50"
            >
              {loading ? (
                <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                "Habilitar"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function Profile() {
  const navigate = useNavigate()
  const { user, refreshUser } = useAuth()
  const toast = useToast()
  const calendar = useCalendarIntegration()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [areas, setAreas] = useState<AreaAtuacaoItem[]>([])
  const [allCities, setAllCities] = useState<CidadeItem[]>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [citySearch, setCitySearch] = useState("")
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false)
  const [loadingCep, setLoadingCep] = useState(false)
  const pendingCityIdRef = useRef<number | null>(null)
  const cityInputRef = useRef<HTMLInputElement>(null)
  const cityDropdownRef = useRef<HTMLDivElement>(null)
  const enderecoRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<UserProfileFormData>({
    defaultValues: {
      pes_nome: "",
      pes_telefone: "",
      pes_email: "",
      pes_instagram: "",
      pes_nascimento: "",
      pes_nome_cracha: "",
      pes_nome_certificado: "",
      pes_cpf: "",
      pes_sexo: "",
      pes_chocolate_preferido: "",
      pes_pare_id: "",
      pes_cep: "",
      pes_endereco: "",
      pes_complemento: "",
      pes_bairro: "",
      pes_cidade: "",
      pes_estado: "",
      pes_classificacao: "",
      pes_tamanho_camisa: "",
      pes_numero_calcado: "",
      pes_numero_sandalia: "",
      pes_numero_bermuda: "",
      pes_tamanho_calca: "",
    },
  })

  const { control, watch, setValue, reset, handleSubmit } = form
  const selectedState = watch("pes_estado")
  const selectedCity = watch("pes_cidade")

  const uniqueStates = useMemo(() => {
    const map = new Map<number, { id: number; sigla: string; nome: string }>()
    allCities.forEach((c) => {
      if (!map.has(c.estado_id)) {
        map.set(c.estado_id, { id: c.estado_id, sigla: c.estado_sigla, nome: c.estado_nome })
      }
    })
    return Array.from(map.values()).sort((a, b) => a.sigla.localeCompare(b.sigla))
  }, [allCities])

  const citiesForState = useMemo(() => {
    if (!selectedState) return []
    const stateId = parseInt(selectedState)
    return allCities.filter((c) => c.estado_id === stateId)
  }, [allCities, selectedState])

  const filteredCities = useMemo(() => {
    if (!citySearch) return citiesForState
    const normalized = normalizeStr(citySearch)
    return citiesForState.filter((c) => normalizeStr(c.nome).includes(normalized))
  }, [citiesForState, citySearch])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(e.target as Node) &&
        cityInputRef.current &&
        !cityInputRef.current.contains(e.target as Node)
      ) {
        setCityDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  useEffect(() => {
    getAreasAtuacao()
      .then((res) => setAreas(res.data))
      .catch((err) => console.error("Erro ao carregar áreas:", err))

    setLoadingCities(true)
    getCidades()
      .then((res) => {
        setAllCities(res.data)
        if (pendingCityIdRef.current) {
          const city = res.data.find((c) => c.id === pendingCityIdRef.current)
          if (city) setCitySearch(city.nome)
          pendingCityIdRef.current = null
        }
      })
      .catch((err) => console.error("Erro ao carregar cidades:", err))
      .finally(() => setLoadingCities(false))
  }, [])

  const loadProfile = () => {
    setIsLoading(true)
    getUserProfile()
      .then(({ data }) => {
        reset({
          pes_nome: data.pes_nome || "",
          pes_telefone: data.pes_telefone || "",
          pes_email: data.pes_email || "",
          pes_instagram: data.pes_instagram || "",
          pes_nascimento: data.pes_nascimento ? data.pes_nascimento.substring(0, 10) : "",
          pes_nome_cracha: data.pes_nome_cracha || "",
          pes_nome_certificado: data.pes_nome_certificado || "",
          pes_cpf: data.pes_cpf || "",
          pes_sexo: data.pes_sexo || "",
          pes_chocolate_preferido: data.pes_chocolate_preferido || "",
          pes_pare_id: data.pes_pare_id != null ? String(data.pes_pare_id) : "",
          pes_cep: data.pes_cep || "",
          pes_endereco: data.pes_endereco || "",
          pes_complemento: data.pes_complemento || "",
          pes_bairro: data.pes_bairro || "",
          pes_cidade: data.pes_cidade ? String(data.pes_cidade) : "",
          pes_estado: data.pes_estado ? String(data.pes_estado) : "",
          pes_classificacao: data.pes_classificacao != null ? String(data.pes_classificacao) : "",
          pes_tamanho_camisa: data.pes_tamanho_camisa || 0,
          pes_numero_calcado: data.pes_numero_calcado || 0,
          pes_numero_sandalia: data.pes_numero_sandalia || 0,
          pes_numero_bermuda: data.pes_numero_bermuda || 0,
          pes_tamanho_calca: data.pes_tamanho_calca || 0,
        })
        if (data.pes_cidade) {
          if (allCities.length > 0) {
            const city = allCities.find((c) => c.id === data.pes_cidade)
            if (city) setCitySearch(city.nome)
          } else {
            pendingCityIdRef.current = data.pes_cidade
          }
        }
      })
      .catch(() => toast.danger("Erro ao carregar perfil."))
      .finally(() => setIsLoading(false))
  }

  useEffect(loadProfile, [reset, allCities])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log(user)
    const file = e.target.files?.[0]

    if (!file || !user?.pesUid) return

    setIsUploadingImage(true)
    try {
      const response = await uploadProfileImage(user.pesUid, file)
      const newImageUrl = response.data.url

      const storedUser = localStorage.getItem("user")
      if (storedUser) {
        const userData = JSON.parse(storedUser)
        userData.pes_foto_url = newImageUrl
        userData.pesFotoUrl = newImageUrl
        localStorage.setItem("user", JSON.stringify(userData))
      }
      refreshUser()
      toast.success("Foto de perfil atualizada!")
    } catch (error) {
      toast.danger("Erro ao fazer upload da imagem.")
    } finally {
      setIsUploadingImage(false)
    }
  }

  const handleCepBlur = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, "")
    if (cleanCep.length !== 8) return
    setLoadingCep(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
      const data = await res.json()
      if (!data.erro) {
        setValue("pes_endereco", data.logradouro || "")
        setValue("pes_bairro", data.bairro || "")
        const uf = (data.uf || "").toUpperCase()
        const matchedState = allCities.find((c) => c.estado_sigla === uf)
        if (matchedState) {
          setValue("pes_estado", String(matchedState.estado_id))
          const normalizedCity = normalizeStr(data.localidade || "")
          const stateCities = allCities.filter((c) => c.estado_id === matchedState.estado_id)
          const cityMatch = stateCities.find((c) => normalizeStr(c.nome) === normalizedCity)
          if (cityMatch) {
            setValue("pes_cidade", String(cityMatch.id))
            setCitySearch(cityMatch.nome)
          } else {
            setCitySearch(data.localidade || "")
          }
        }
        setTimeout(() => enderecoRef.current?.focus(), 200)
      }
    } catch {
    } finally {
      setLoadingCep(false)
    }
  }

  const handleSelectCity = (city: CidadeItem) => {
    setValue("pes_cidade", String(city.id))
    setCitySearch(city.nome)
    setCityDropdownOpen(false)
  }

  const onSubmit = async (formData: UserProfileFormData) => {
    setIsSaving(true)
    try {
      const selectedArea = areas.find((a) => String(a.pare_id) === formData.pes_pare_id)
      await updateUserProfile({
        ...formData,
        pes_telefone: formData.pes_telefone ? formData.pes_telefone.replace(/\D/g, "") : "",
        pes_cpf: formData.pes_cpf ? formData.pes_cpf.replace(/\D/g, "") : "",
        pes_pare_id: formData.pes_pare_id ? parseInt(formData.pes_pare_id) : 0,
        pare_descricao: selectedArea?.pare_descricao || "",
        pes_cep: formData.pes_cep ? formData.pes_cep.replace(/\D/g, "") : "",
        pes_cidade: formData.pes_cidade ? parseInt(formData.pes_cidade) : 0,
        pes_estado: formData.pes_estado ? parseInt(formData.pes_estado) : 0,
        pes_classificacao: formData.pes_classificacao ? parseInt(formData.pes_classificacao) : 0,
      })
      toast.success("Perfil atualizado com sucesso!")
      setIsEditing(false)
    } catch {
      toast.danger("Erro ao salvar perfil.")
    } finally {
      setIsSaving(false)
    }
  }

  if (!user) return <div>Carregando...</div>

  const values = form.getValues()
  const areaDesc = areas.find(
    (a) => a.pare_id === parseInt(values.pes_pare_id || "", 10)
  )?.pare_descricao
  const stateDesc = uniqueStates.find((s) => s.id === parseInt(values.pes_estado || "", 10))?.nome
  const cityDesc = allCities.find((c) => c.id === parseInt(values.pes_cidade || "", 10))?.nome
  const memberTypeDesc = MEMBER_TYPES.find((t) => t.value === values.pes_classificacao)?.label

  return (
    <>
      <main className="w-full animate-fade-in space-y-4 overflow-x-hidden p-4 pb-28 sm:space-y-6 sm:p-6 sm:pb-6">
        <div className="mx-auto space-y-4 sm:space-y-6 md:max-w-3xl">
          <div className="card-elevated overflow-hidden p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 overflow-hidden sm:gap-4">
                <div
                  className="group relative flex size-12 shrink-0 items-center justify-center rounded-full bg-muted sm:size-20"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {!isEditing && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      {isUploadingImage ? (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-black/40">
                          <div className="size-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        </div>
                      ) : (
                        <div className="absolute -right-2 bottom-0 z-10 flex cursor-pointer items-center justify-center rounded-full transition-colors">
                          <div className="flex size-8 items-center justify-center rounded-full border bg-zinc-900 text-orange-500">
                            <Icon
                              name="camera"
                              className="text-orange-500 opacity-0 transition-opacity group-hover:opacity-100"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {user.pesFotoUrl ? (
                    <img
                      src={user.pesFotoUrl}
                      alt={user.pesNome}
                      className="size-full rounded-full object-cover"
                    />
                  ) : (
                    <i className="fi fi-ts-user flex text-2xl text-foreground sm:text-3xl" />
                  )}
                </div>
                <div className="mr-3 min-w-0">
                  <h1 className="truncate text-xl font-bold text-foreground sm:text-2xl">
                    Meu Perfil
                  </h1>
                  <p className="truncate text-xs text-muted-foreground sm:text-sm">
                    {user.pesEmail}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {(user.pesTipo === 3 || user.pesTipo === 5) && (
                  <Button
                    onClick={() => navigate("/face-capture")}
                    className="sm:flex"
                  >
                    <span className="hidden sm:flex">Credenciamento</span>
                    <Icon
                      name="face-viewfinder"
                      state="elegant"
                      size={20}
                    />
                  </Button>
                )}
                {!isEditing && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="sm:flex"
                  >
                    <span className="hidden sm:flex">Editar Perfil</span>
                    <Icon
                      name="pen-circle"
                      state="elegant"
                      size={20}
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="card-premium flex items-center justify-center p-12">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">Carregando dados...</span>
              </div>
            </div>
          ) : isEditing ? (
            <Form {...form}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-4 sm:space-y-6"
              >
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                    <i className="fi fi-ts-user flex" />
                    Dados Pessoais
                  </h3>
                  <div className="card-premium space-y-4 p-3 sm:p-4">
                    <FormField
                      control={control}
                      name="pes_nome"
                      rules={{ required: "O nome é obrigatório" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome Completo</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Seu nome completo"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="pes_telefone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Whatsapp</FormLabel>
                            <FormControl>
                              <InputMask
                                mask="99 (99) 99999-9999"
                                value={field.value}
                                onChange={field.onChange}
                              >
                                {(inputProps: any) => (
                                  <Input
                                    {...inputProps}
                                    placeholder="(00) 00000-0000"
                                    inputMode="tel"
                                  />
                                )}
                              </InputMask>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="pes_email"
                        rules={{ required: "O email é obrigatório" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                inputMode="email"
                                placeholder="seu@email.com"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="pes_instagram"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Instagram</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                                  @
                                </span>
                                <Input
                                  className="pl-8"
                                  placeholder="seuusuario"
                                  value={field.value}
                                  onChange={(e) => field.onChange(e.target.value.replace(/^@/, ""))}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="pes_nascimento"
                        rules={{ required: "Data de nascimento é obrigatória" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Data de Nascimento</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="pes_nome_cracha"
                        rules={{ required: "O nome no crachá é obrigatório" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nome para Crachá</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Nome no crachá"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="pes_nome_certificado"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nome para Certificado</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Nome no certificado"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <FormField
                        control={control}
                        name="pes_cpf"
                        rules={{ required: "O documento é obrigatório" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>CPF</FormLabel>
                            <FormControl>
                              <InputMask
                                mask="999.999.999-99"
                                value={field.value}
                                onChange={field.onChange}
                              >
                                {(inputProps: any) => (
                                  <Input
                                    {...inputProps}
                                    placeholder="000.000.000-00"
                                    inputMode="numeric"
                                  />
                                )}
                              </InputMask>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="pes_sexo"
                        rules={{ required: "O sexo é obrigatório" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sexo</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="M">Masculino</SelectItem>
                                <SelectItem value="F">Feminino</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {/* <FormField
                        control={control}
                        name="pes_chocolate_preferido"
                        rules={{ required: "O chocolate favorito é obrigatório" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Chocolate Favorito</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Ex: Lacta ao leite"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      /> */}
                    </div>
                    <FormField
                      control={control}
                      name="pes_pare_id"
                      rules={{ required: "A área de atuação é obrigatória" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Área de Atuação</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione a área" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {areas
                                .filter((a) => a.pare_id != null)
                                .map((a) => (
                                  <SelectItem
                                    key={a.pare_id}
                                    value={String(a.pare_id)}
                                  >
                                    {a.pare_descricao}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2 sm:space-y-3">
                  <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                    <i className="fi fi-ts-marker flex" />
                    Endereço
                  </h3>
                  <div className="card-premium space-y-4 p-3 sm:p-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <FormField
                        control={control}
                        name="pes_cep"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>CEP</FormLabel>
                            <FormControl>
                              <InputMask
                                mask="99999-999"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                  handleCepBlur(e.target.value)
                                }
                              >
                                {(inputProps: any) => (
                                  <Input
                                    {...inputProps}
                                    placeholder="00000-000"
                                    inputMode="numeric"
                                    className={loadingCep ? "animate-pulse" : ""}
                                  />
                                )}
                              </InputMask>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="sm:col-span-2">
                        <FormField
                          control={control}
                          name="pes_endereco"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Endereço/Nº</FormLabel>
                              <FormControl>
                                <Input
                                  ref={enderecoRef}
                                  placeholder="Rua, número"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="pes_complemento"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Complemento</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Apto, bloco, sala..."
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="pes_bairro"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Bairro</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Bairro"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="pes_estado"
                        rules={{ required: "O estado é obrigatório" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Estado</FormLabel>
                            <Select
                              onValueChange={(val) => {
                                field.onChange(val)
                                setValue("pes_cidade", "")
                                setCitySearch("")
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={
                                      loadingCities ? "Carregando..." : "Selecione o estado"
                                    }
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {uniqueStates.map((s) => (
                                  <SelectItem
                                    key={s.id}
                                    value={String(s.id)}
                                  >
                                    {s.sigla} - {s.nome}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="pes_cidade"
                        rules={{ required: "A cidade é obrigatória" }}
                        render={() => (
                          <FormItem>
                            <FormLabel>Cidade</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <i className="fi fi-ts-search absolute left-3 top-1/2 flex -translate-y-1/2 text-xs text-muted-foreground" />
                                <Input
                                  ref={cityInputRef}
                                  className="pl-8"
                                  placeholder={
                                    loadingCities
                                      ? "Carregando..."
                                      : !selectedState
                                        ? "Selecione o estado"
                                        : "Buscar cidade..."
                                  }
                                  disabled={!selectedState || loadingCities}
                                  value={citySearch}
                                  onChange={(e) => {
                                    setCitySearch(e.target.value)
                                    setCityDropdownOpen(true)
                                    if (!e.target.value) setValue("pes_cidade", "")
                                  }}
                                  onFocus={() => {
                                    if (selectedState && citiesForState.length > 0)
                                      setCityDropdownOpen(true)
                                  }}
                                />
                                {selectedCity && (
                                  <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    onClick={() => {
                                      setValue("pes_cidade", "")
                                      setCitySearch("")
                                      cityInputRef.current?.focus()
                                    }}
                                  >
                                    <i className="fi fi-ts-cross-small flex text-xs" />
                                  </button>
                                )}
                                {cityDropdownOpen && filteredCities.length > 0 && (
                                  <div
                                    ref={cityDropdownRef}
                                    className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md"
                                  >
                                    {filteredCities.map((city) => (
                                      <button
                                        key={city.id}
                                        type="button"
                                        className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${
                                          selectedCity === String(city.id)
                                            ? "bg-accent font-medium text-accent-foreground"
                                            : ""
                                        }`}
                                        onClick={() => handleSelectCity(city)}
                                      >
                                        {city.nome}
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {cityDropdownOpen &&
                                  citySearch &&
                                  filteredCities.length === 0 &&
                                  !loadingCities && (
                                    <div
                                      ref={cityDropdownRef}
                                      className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-3 text-center text-sm text-muted-foreground shadow-md"
                                    >
                                      Nenhuma cidade encontrada
                                    </div>
                                  )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {user.pesTipo === 3 && (
                  <div className="space-y-2 sm:space-y-3">
                    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                      <i className="fi fi-ts-star flex" />
                      Dados Giants
                    </h3>
                    <div className="card-premium space-y-4 p-3 sm:p-4">
                      <FormField
                        control={control}
                        name="pes_classificacao"
                        rules={{ required: "O tipo é obrigatório" }}
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Tipo</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-wrap gap-2"
                              >
                                {MEMBER_TYPES.map((option) => (
                                  <FormItem
                                    key={option.value}
                                    className="flex items-center space-x-0"
                                  >
                                    <FormControl>
                                      <RadioGroupItem
                                        value={option.value}
                                        id={`pes_classificacao-${option.value}`}
                                        className="sr-only"
                                      />
                                    </FormControl>
                                    <FormLabel
                                      htmlFor={`pes_classificacao-${option.value}`}
                                      className={cn(
                                        "cursor-pointer rounded-md border px-4 py-2 text-sm transition-colors",
                                        "border-gray-200 hover:bg-gray-100 hover:text-zinc-900",
                                        field.value === option.value &&
                                          "border-orange-500 bg-orange-500/10 font-semibold text-orange-600"
                                      )}
                                    >
                                      {option.label}
                                    </FormLabel>
                                  </FormItem>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="grid grid-cols-1 gap-4">
                        <RadioGroupField
                          control={control}
                          name="pes_tamanho_camisa"
                          label="Camisa"
                          options={SIZES}
                        />
                        <RadioGroupField
                          control={control}
                          name="pes_numero_bermuda"
                          label="Bermuda"
                          options={SIZES}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={control}
                          name="pes_numero_calcado"
                          rules={{ required: "O número do calçado é obrigatório" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Calçado</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  inputMode="numeric"
                                  min="30"
                                  max="50"
                                  placeholder="Ex: 42"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={control}
                          name="pes_numero_sandalia"
                          rules={{ required: "O número da sandália é obrigatório" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Sandália</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  inputMode="numeric"
                                  min="30"
                                  max="50"
                                  placeholder="Nº"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={control}
                          name="pes_tamanho_calca"
                          rules={{ required: "O tamanho da calça é obrigatório" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Calça</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  inputMode="numeric"
                                  min="30"
                                  max="60"
                                  placeholder="Nº"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="bottom-0 left-0 z-40 flex w-full gap-2 bg-background/95 p-3 backdrop-blur-sm sm:relative sm:inset-auto sm:z-auto sm:w-auto sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false)
                      loadProfile()
                    }}
                    className="flex-1 sm:flex-initial"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="flex-1 sm:flex-initial"
                  >
                    {isSaving ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Salvando...
                      </>
                    ) : (
                      "Salvar Alterações"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-2 sm:space-y-3">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                  <i className="fi fi-ts-user flex" />
                  Dados Pessoais
                </h3>
                <div className="card-premium grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 sm:p-4">
                  <FormFieldDisplay
                    label="Nome Completo"
                    value={values.pes_nome}
                  />
                  <FormFieldDisplay
                    label="Whatsapp"
                    value={values.pes_telefone}
                  />
                  <FormFieldDisplay
                    label="Email"
                    value={values.pes_email}
                  />
                  <FormFieldDisplay
                    label="Instagram"
                    value={values.pes_instagram ? `@${values.pes_instagram}` : "-"}
                  />
                  <FormFieldDisplay
                    label="Data de Nascimento"
                    value={values.pes_nascimento.split("-").reverse().join("/")}
                  />
                  <FormFieldDisplay
                    label="Nome para Crachá"
                    value={values.pes_nome_cracha}
                  />
                  <FormFieldDisplay
                    label="Nome para Certificado"
                    value={values.pes_nome_certificado}
                  />
                  <FormFieldDisplay
                    label="CPF/NIF"
                    value={values.pes_cpf}
                  />
                  <FormFieldDisplay
                    label="Sexo"
                    value={values.pes_sexo === "M" ? "Masculino" : "Feminino"}
                  />
                  {/* <FormFieldDisplay
                    label="Chocolate Favorito"
                    value={values.pes_chocolate_preferido}
                  /> */}
                  <FormFieldDisplay
                    label="Área de Atuação"
                    value={areaDesc}
                  />
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                  <i className="fi fi-ts-marker flex" />
                  Endereço
                </h3>
                <div className="card-premium grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 sm:p-4">
                  <FormFieldDisplay
                    label="CEP"
                    value={values.pes_cep}
                  />
                  <FormFieldDisplay
                    label="Endereço"
                    value={values.pes_endereco}
                  />
                  <FormFieldDisplay
                    label="Complemento"
                    value={values.pes_complemento}
                  />
                  <FormFieldDisplay
                    label="Bairro"
                    value={values.pes_bairro}
                  />
                  <FormFieldDisplay
                    label="Estado"
                    value={stateDesc}
                  />
                  <FormFieldDisplay
                    label="Cidade"
                    value={cityDesc}
                  />
                </div>
              </div>

              {user.pesTipo === 3 && (
                <>
                  <div className="space-y-2 sm:space-y-3">
                    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                      <i className="fi fi-ts-star flex" />
                      Dados Giants
                    </h3>
                    <div className="card-premium grid grid-cols-2 gap-4 p-3 sm:p-4">
                      <FormFieldDisplay
                        label="Tipo"
                        value={memberTypeDesc}
                      />
                      <FormFieldDisplay
                        label="Camisa"
                        value={values.pes_tamanho_camisa}
                      />
                      <FormFieldDisplay
                        label="Calçado"
                        value={values.pes_numero_calcado}
                      />
                      <FormFieldDisplay
                        label="Sandália"
                        value={values.pes_numero_sandalia}
                      />
                      <FormFieldDisplay
                        label="Bermuda"
                        value={values.pes_numero_bermuda}
                      />
                      <FormFieldDisplay
                        label="Calça"
                        value={values.pes_tamanho_calca}
                      />
                    </div>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                      <i className="fi fi-ts-users flex" />
                      Meu Time Giants
                    </h3>
                    <div className="card-premium space-y-4 p-3 sm:p-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-xs text-muted-foreground sm:text-sm">
                          Gerente de Sucesso (GS)
                        </span>
                        <div>
                          <p className="font-semibold">{user.gs_nome}</p>
                          <p className="text-xs text-muted-foreground sm:text-sm">
                            {user.gs_email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`https://wa.me/55${user.gs_fone}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex size-10 items-center justify-center rounded-full bg-muted transition-colors hover:bg-muted/80"
                          >
                            <i className="fi fi-brands-whatsapp flex text-xl text-green-500" />
                          </a>
                          <a
                            href={`mailto:${user.gs_email}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex size-10 items-center justify-center rounded-full bg-muted transition-colors hover:bg-muted/80"
                          >
                            <i className="fi fi-ts-envelope flex text-xl text-foreground" />
                          </a>
                        </div>
                      </div>
                      <div className="border-b border-border" />
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Diretor</span>
                        <span className="font-medium text-foreground">{user.diretor_nome}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Mentor</span>
                        <span className="font-medium text-foreground">{user.mentor_nome}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Seção de Integrações de Calendário (oculta em dispositivos móveis)*/}
          <div className="hidden space-y-2 sm:space-y-3 md:block">
            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
              <i className="fi fi-ts-calendar-lines flex" />
              Integrações de Calendário
            </h3>
            <div className="card-premium space-y-3 p-3 sm:p-4">
              <p className="text-sm text-zinc-400">
                Conecte seus calendários para sincronizar eventos automaticamente ao se inscrever.
              </p>

              {/* Google Calendar*/}
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center gap-3">
                  <svg
                    viewBox="0 0 24 24"
                    className="size-6 shrink-0"
                    fill="none"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-white">Google Calendar</p>
                    <p className="text-xs text-zinc-500">
                      {calendar.googleConnected ? "Conta conectada" : "Não conectado"}
                    </p>
                  </div>
                </div>
                {calendar.googleConnected ? (
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                      ativo
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={calendar.disconnectGoogle}
                      className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400"
                    >
                      Desconectar
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={calendar.loadingGoogle}
                    onClick={async () => {
                      try {
                        await calendar.connectGoogle()
                        toast.success("Google Calendar conectado com sucesso!")
                      } catch (err: any) {
                        toast.danger(err.message || "Erro ao conectar Google Calendar")
                      }
                    }}
                    className="shrink-0 border-white/10 bg-transparent text-white hover:bg-white/10"
                  >
                    {calendar.loadingGoogle ? (
                      <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      "Conectar"
                    )}
                  </Button>
                )}
              </div>

              {/* Microsoft Calendar*/}
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center gap-3">
                  <svg
                    viewBox="0 0 23 23"
                    className="size-6 shrink-0"
                  >
                    <path
                      fill="#f3f3f3"
                      d="M0 0h23v23H0z"
                    />
                    <path
                      fill="#f35325"
                      d="M1 1h10v10H1z"
                    />
                    <path
                      fill="#81bc06"
                      d="M12 1h10v10H12z"
                    />
                    <path
                      fill="#05a6f0"
                      d="M1 12h10v10H1z"
                    />
                    <path
                      fill="#ffba08"
                      d="M12 12h10v10H12z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-white">Microsoft Calendar</p>
                    <p className="text-xs text-zinc-500">
                      {calendar.microsoftConnected ? "Conta conectada" : "Não conectado"}
                    </p>
                  </div>
                </div>
                {calendar.microsoftConnected ? (
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                      ativo
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={calendar.disconnectMicrosoft}
                      className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400"
                    >
                      Desconectar
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={calendar.loadingMicrosoft}
                    onClick={async () => {
                      try {
                        await calendar.connectMicrosoft()
                        toast.success("Microsoft Calendar conectado com sucesso!")
                      } catch (err: any) {
                        toast.danger(err.message || "Erro ao conectar Microsoft Calendar")
                      }
                    }}
                    className="shrink-0 border-white/10 bg-transparent text-white hover:bg-white/10"
                  >
                    {calendar.loadingMicrosoft ? (
                      <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      "Conectar"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Seção de Face ID — apenas no app nativo */}
          {typeof window !== "undefined" && !!(window as any).ReactNativeWebView && (
            <FaceIdSection />
          )}
        </div>
      </main>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
      />
    </>
  )
}
